/**
 * Habitat tile definitions.
 *
 * Phase 1 ships a representative subset that:
 * - covers all 5 habitats on at least one edge,
 * - covers all 5 wildlife species in `wildlifeOptions` somewhere,
 * - includes both keystone (single-habitat, single-wildlife) and dual-habitat
 *   tiles, and
 * - includes the tiles needed by the sample-scenario fixtures (P2).
 *
 * The full ~85+45 tile catalog lands with P2 fixtures and is intentionally
 * not committed at Phase 1 — this is the type-shape pass.
 *
 * Edge order: `[top-right, right, bottom-right, bottom-left, left, top-left]`.
 */

import type { TileDef } from "../types/tile";

const hex = (h: TileDef["edges"][number]): TileDef["edges"] =>
  [h, h, h, h, h, h] as const;

export const TILE_DEFS: readonly TileDef[] = [
  // Keystones — one per habitat × representative wildlife
  {
    tileDefId: "tile_forest_keystone_bear",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("forest"),
    wildlifeOptions: ["bear"],
    keystone: true,
    artKey: "tile/forest_bear",
  },
  {
    tileDefId: "tile_forest_keystone_elk",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("forest"),
    wildlifeOptions: ["elk"],
    keystone: true,
    artKey: "tile/forest_elk",
  },
  {
    tileDefId: "tile_forest_keystone_hawk",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("forest"),
    wildlifeOptions: ["hawk"],
    keystone: true,
    artKey: "tile/forest_hawk",
  },
  {
    tileDefId: "tile_forest_keystone_salmon",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("forest"),
    wildlifeOptions: ["salmon"],
    keystone: true,
    artKey: "tile/forest_salmon",
  },
  {
    tileDefId: "tile_mountain_keystone_hawk",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("mountain"),
    wildlifeOptions: ["hawk"],
    keystone: true,
    artKey: "tile/mountain_hawk",
  },
  {
    tileDefId: "tile_mountain_keystone_bear",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("mountain"),
    wildlifeOptions: ["bear"],
    keystone: true,
    artKey: "tile/mountain_bear",
  },
  {
    tileDefId: "tile_mountain_keystone_fox",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("mountain"),
    wildlifeOptions: ["fox"],
    keystone: true,
    artKey: "tile/mountain_fox",
  },
  {
    tileDefId: "tile_prairie_keystone_fox",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("prairie"),
    wildlifeOptions: ["fox"],
    keystone: true,
    artKey: "tile/prairie_fox",
  },
  {
    tileDefId: "tile_wetland_keystone_bear",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("wetland"),
    wildlifeOptions: ["bear"],
    keystone: true,
    artKey: "tile/wetland_bear",
  },
  {
    tileDefId: "tile_river_keystone_salmon",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("river"),
    wildlifeOptions: ["salmon"],
    keystone: true,
    artKey: "tile/river_salmon",
  },
  {
    tileDefId: "tile_river_keystone_fox",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("river"),
    wildlifeOptions: ["fox"],
    keystone: true,
    artKey: "tile/river_fox",
  },

  // Dual-habitat — split half-half on alternating edges, multi-wildlife
  {
    tileDefId: "tile_forest_river_split_bear_hawk",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["forest", "forest", "forest", "river", "river", "river"],
    wildlifeOptions: ["bear", "hawk"],
    keystone: false,
    artKey: "tile/forest_river_split",
  },
  {
    tileDefId: "tile_wetland_river_split_bear_hawk_salmon",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["wetland", "wetland", "river", "river", "river", "wetland"],
    wildlifeOptions: ["bear", "hawk", "salmon"],
    keystone: false,
    artKey: "tile/wetland_river_split",
  },
  {
    tileDefId: "tile_prairie_mountain_split_elk_fox",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: [
      "prairie",
      "prairie",
      "prairie",
      "mountain",
      "mountain",
      "mountain",
    ],
    wildlifeOptions: ["elk", "fox"],
    keystone: false,
    artKey: "tile/prairie_mountain_split",
  },

  // -------------------------------------------------------------------------
  // Starter cluster tiles (§3.7 / §6.1.3)
  //
  // The 5 published starters. Each is a 3-hex triangle:
  //   _key — single-habitat keystone (one wildlife option, nature-token tile)
  //   _d5  — dual-habitat (two wildlife options)
  //   _d7  — dual-habitat (three wildlife options)
  // Dual edges follow the engine convention: first habitat = edges 0..2,
  // second = edges 3..5. No wildlife is pre-placed; markers are options.
  // -------------------------------------------------------------------------

  // Starter 01
  {
    tileDefId: "tile_start01_key",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("wetland"),
    wildlifeOptions: ["hawk"],
    keystone: true,
    artKey: "tile/start01_key",
  },
  {
    tileDefId: "tile_start01_d5",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: [
      "prairie",
      "prairie",
      "prairie",
      "mountain",
      "mountain",
      "mountain",
    ],
    wildlifeOptions: ["bear", "fox"],
    keystone: false,
    artKey: "tile/start01_d5",
  },
  {
    tileDefId: "tile_start01_d7",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["river", "river", "river", "forest", "forest", "forest"],
    wildlifeOptions: ["salmon", "elk", "hawk"],
    keystone: false,
    artKey: "tile/start01_d7",
  },

  // Starter 02
  {
    tileDefId: "tile_start02_key",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("mountain"),
    wildlifeOptions: ["bear"],
    keystone: true,
    artKey: "tile/start02_key",
  },
  {
    tileDefId: "tile_start02_d5",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["river", "river", "river", "prairie", "prairie", "prairie"],
    wildlifeOptions: ["salmon", "bear"],
    keystone: false,
    artKey: "tile/start02_d5",
  },
  {
    tileDefId: "tile_start02_d7",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["forest", "forest", "forest", "wetland", "wetland", "wetland"],
    wildlifeOptions: ["hawk", "elk", "fox"],
    keystone: false,
    artKey: "tile/start02_d7",
  },

  // Starter 03
  {
    tileDefId: "tile_start03_key",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("forest"),
    wildlifeOptions: ["elk"],
    keystone: true,
    artKey: "tile/start03_key",
  },
  {
    tileDefId: "tile_start03_d5",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["wetland", "wetland", "wetland", "prairie", "prairie", "prairie"],
    wildlifeOptions: ["fox", "salmon"],
    keystone: false,
    artKey: "tile/start03_d5",
  },
  {
    tileDefId: "tile_start03_d7",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["mountain", "mountain", "mountain", "river", "river", "river"],
    wildlifeOptions: ["hawk", "elk", "bear"],
    keystone: false,
    artKey: "tile/start03_d7",
  },

  // Starter 04
  {
    tileDefId: "tile_start04_key",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("river"),
    wildlifeOptions: ["salmon"],
    keystone: true,
    artKey: "tile/start04_key",
  },
  {
    tileDefId: "tile_start04_d5",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: [
      "mountain",
      "mountain",
      "mountain",
      "wetland",
      "wetland",
      "wetland",
    ],
    wildlifeOptions: ["fox", "hawk"],
    keystone: false,
    artKey: "tile/start04_d5",
  },
  {
    tileDefId: "tile_start04_d7",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["prairie", "prairie", "prairie", "forest", "forest", "forest"],
    wildlifeOptions: ["salmon", "elk", "bear"],
    keystone: false,
    artKey: "tile/start04_d7",
  },

  // Starter 05
  {
    tileDefId: "tile_start05_key",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: hex("prairie"),
    wildlifeOptions: ["fox"],
    keystone: true,
    artKey: "tile/start05_key",
  },
  {
    tileDefId: "tile_start05_d5",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["forest", "forest", "forest", "mountain", "mountain", "mountain"],
    wildlifeOptions: ["bear", "elk"],
    keystone: false,
    artKey: "tile/start05_d5",
  },
  {
    tileDefId: "tile_start05_d7",
    shape: "single_hex",
    orientation: "pointy-top",
    edges: ["wetland", "wetland", "wetland", "river", "river", "river"],
    wildlifeOptions: ["salmon", "elk", "fox"],
    keystone: false,
    artKey: "tile/start05_d7",
  },
];

export const TILE_DEFS_BY_ID: Readonly<Record<string, TileDef>> = Object.freeze(
  Object.fromEntries(TILE_DEFS.map((tile) => [tile.tileDefId, tile])),
);
